<template>
    <v-container fluid class="manutencao-page pa-8 pt-12">
        <div class="manutencao-header">
            <div>
                <h1 class="manutencao-title">Manutenção de Veículos</h1>
                <p class="manutencao-subtitle">
                    Acompanhe serviços preventivos e corretivos da frota em um único lugar.
                </p>
            </div>
            <v-btn class="manutencao-new-btn" depressed @click="abrirNovaManutencao">
                <v-icon left size="18">mdi-plus</v-icon>
                Nova manutenção
            </v-btn>
        </div>

        <v-row class="mt-6" dense>
            <v-col cols="12" md="3">
                <v-card class="manutencao-stat" outlined>
                    <div class="manutencao-stat__content">
                        <div>
                            <div class="manutencao-stat__label">Total de registros</div>
                            <div class="manutencao-stat__value">{{ manutencoes.length }}</div>
                        </div>
                        <div class="manutencao-stat__icon manutencao-stat__icon--primary">
                            <v-icon color="#10b981" size="20">mdi-wrench-outline</v-icon>
                        </div>
                    </div>
                </v-card>
            </v-col>
            <v-col cols="12" md="3">
                <v-card class="manutencao-stat" outlined>
                    <div class="manutencao-stat__content">
                        <div>
                            <div class="manutencao-stat__label">Em aberto</div>
                            <div class="manutencao-stat__value manutencao-stat__value--accent">{{ totalEmAberto }}</div>
                        </div>
                        <div class="manutencao-stat__icon manutencao-stat__icon--accent">
                            <v-icon color="#2563eb" size="20">mdi-progress-clock</v-icon>
                        </div>
                    </div>
                </v-card>
            </v-col>
            <v-col cols="12" md="3">
                <v-card class="manutencao-stat" outlined>
                    <div class="manutencao-stat__content">
                        <div>
                            <div class="manutencao-stat__label">Concluídas</div>
                            <div class="manutencao-stat__value">{{ totalConcluidas }}</div>
                        </div>
                        <div class="manutencao-stat__icon manutencao-stat__icon--muted">
                            <v-icon color="#64748b" size="20">mdi-check-decagram-outline</v-icon>
                        </div>
                    </div>
                </v-card>
            </v-col>
            <v-col cols="12" md="3">
                <v-card class="manutencao-stat" outlined>
                    <div class="manutencao-stat__content">
                        <div>
                            <div class="manutencao-stat__label">Custo total</div>
                            <div class="manutencao-stat__value">{{ formatCurrency(custoTotal) }}</div>
                        </div>
                        <div class="manutencao-stat__icon manutencao-stat__icon--warning">
                            <v-icon color="#f59e0b" size="20">mdi-currency-usd</v-icon>
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <v-card class="manutencao-table-card mt-6" outlined>
            <div class="manutencao-table-card__toolbar">
                <v-badge
                    :value="hasActiveFilters"
                    color="red"
                    dot
                    overlap
                >
                    <v-btn
                        icon
                        class="manutencao-icon-btn"
                        title="Filtros"
                        aria-label="Filtros"
                        @click="abrirFiltros"
                    >
                        <v-icon size="18">mdi-filter-outline</v-icon>
                    </v-btn>
                </v-badge>
            </div>
            <v-data-table
                class="manutencao-table"
                :headers="headers"
                :items="manutencoesFiltradas"
                :loading="loading"
                loading-text="Carregando manutenções..."
                no-data-text="Nenhuma manutenção cadastrada"
                :items-per-page="8"
                :footer-props="footerProps"
            >
                <template v-slot:[`item.veiculo`]='{ item }'>
                    <div class="manutencao-veiculo-cell">
                        <strong>{{ item.veiculo_placa || '-' }}</strong>
                        <span>{{ item.veiculo_modelo || 'Veículo sem modelo' }}</span>
                    </div>
                </template>

                <template v-slot:[`item.status`]='{ item }'>
                    <span class="manutencao-status" :class="getStatusClass(item.status)">
                        {{ item.status || '-' }}
                    </span>
                </template>

                <template v-slot:[`item.valor`]='{ item }'>
                    <strong>{{ formatCurrency(item.valor) }}</strong>
                </template>

                <template v-slot:[`item.data_manutencao`]='{ item }'>
                    <span>{{ formatDate(item.data_manutencao) }}</span>
                </template>

                <template v-slot:[`item.acoes`]='{ item }'>
                   <v-menu offset-y left>
                       <template v-slot:activator="{ on, attrs }">
                           <v-btn icon small class="manutencao-actions" v-bind="attrs" v-on="on">
                               <v-icon color="#10b981">mdi-dots-vertical</v-icon>
                           </v-btn>
                       </template>
                       <v-list dense class="manutencao-actions__menu">
                           <v-list-item @click="abrirDialogDetalhes(item)">
                               <v-list-item-icon>
                                   <v-icon color="#2563eb" size="18">mdi-eye-outline</v-icon>
                               </v-list-item-icon>
                               <v-list-item-title>Detalhes</v-list-item-title>
                           </v-list-item>
                           <v-list-item @click="editarManutencao(item)">
                               <v-list-item-icon>
                                   <v-icon color="#10b981" size="18">mdi-pencil-outline</v-icon>
                               </v-list-item-icon>
                               <v-list-item-title>Editar</v-list-item-title>
                           </v-list-item>
                           <v-divider />
                           <v-list-item @click="abrirDialogExcluir(item)">
                               <v-list-item-icon>
                                   <v-icon color="#ef4444" size="18">mdi-delete-outline</v-icon>
                               </v-list-item-icon>
                               <v-list-item-title>Excluir</v-list-item-title>
                           </v-list-item>
                       </v-list>
                   </v-menu>
                </template>
            </v-data-table>
        </v-card>

        <v-dialog v-model="filterDialogOpen" max-width="480" persistent>
            <v-card class="manutencao-dialog" outlined>
                <v-card-title class="manutencao-dialog__title">
                    <span>Filtrar manutenções</span>
                    <v-spacer />
                    <v-btn icon @click="filterDialogOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-6 pb-0">
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-select
                                v-model="filterForm.status"
                                :items="statusFiltroOptions"
                                label="Status"
                                outlined
                                dense
                                clearable
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="filterForm.veiculo_id"
                                :items="veiculoFilterOptions"
                                item-text="label"
                                item-value="value"
                                label="Veículo"
                                outlined
                                dense
                                clearable
                                no-data-text="Nenhum veículo encontrado"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-autocomplete
                                v-model="filterForm.tipo"
                                :items="tiposFiltroOptions"
                                label="Tipo"
                                outlined
                                dense
                                clearable
                                no-data-text="Nenhum tipo encontrado"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="filterForm.data_de"
                                label="Data de"
                                type="date"
                                outlined
                                dense
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                v-model="filterForm.data_ate"
                                label="Data até"
                                type="date"
                                outlined
                                dense
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-btn text @click="limparFiltros">Limpar</v-btn>
                    <v-spacer />
                    <v-btn text @click="filterDialogOpen = false">Cancelar</v-btn>
                    <v-btn class="manutencao-new-btn" depressed @click="aplicarFiltros">
                        Filtrar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="detalhesDialogOpen" max-width="600" persistent>
           <v-card class="manutencao-dialog" outlined>
               <v-card-title class="manutencao-dialog__title">
                   <span>Detalhes da Manutenção</span>
                   <v-spacer />
                   <v-btn icon @click="detalhesDialogOpen = false">
                       <v-icon>mdi-close</v-icon>
                   </v-btn>
               </v-card-title>
               <v-divider />
               <v-card-text class="pt-6 pb-0">
                   <v-simple-table dense>
                       <tbody>
                           <tr><td><b>Veículo</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.veiculo_placa || '-' }}</td></tr>
                           <tr><td><b>Modelo</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.veiculo_modelo || '-' }}</td></tr>
                           <tr><td><b>Tipo</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.tipo || '-' }}</td></tr>
                           <tr><td><b>Status</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.status || '-' }}</td></tr>
                           <tr><td><b>Data da manutenção</b></td><td>{{ formatDate(detalhesSelecionado && detalhesSelecionado.data_manutencao) }}</td></tr>
                           <tr><td><b>Data de conclusão</b></td><td>{{ formatDate(detalhesSelecionado && detalhesSelecionado.data_conclusao) }}</td></tr>
                           <tr><td><b>Descrição</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.descricao || '-' }}</td></tr>
                           <tr><td><b>Fornecedor</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.fornecedor || '-' }}</td></tr>
                           <tr><td><b>Quilometragem</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.quilometragem || '-' }}</td></tr>
                           <tr><td><b>Valor</b></td><td>{{ formatCurrency(detalhesSelecionado && detalhesSelecionado.valor) }}</td></tr>
                           <tr><td><b>Observações</b></td><td>{{ detalhesSelecionado && detalhesSelecionado.observacoes || '-' }}</td></tr>
                       </tbody>
                   </v-simple-table>
               </v-card-text>
               <v-card-actions class="px-6 pb-6">
                   <v-spacer />
                   <v-btn text @click="detalhesDialogOpen = false">Fechar</v-btn>
               </v-card-actions>
           </v-card>
        </v-dialog>

        <v-dialog v-model="dialogOpen" max-width="760" persistent>
            <v-card class="manutencao-dialog" outlined>
                <v-card-title class="manutencao-dialog__title">
                    <span>{{ dialogTitle }}</span>
                    <v-spacer />
                    <v-btn icon :disabled="saving" @click="closeDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-6 pb-0">
                    <v-form ref="manutencaoForm" v-model="formValid" lazy-validation>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="form.veiculo_id"
                                    :items="veiculosOptions"
                                    item-text="label"
                                    item-value="value"
                                    label="Veículo"
                                    outlined
                                    dense
                                    :rules="rules.veiculo_id"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-combobox
                                    v-model="form.tipo"
                                    :items="tiposManutencao"
                                    label="Tipo"
                                    outlined
                                    dense
                                    :rules="rules.tipo"
                                />
                            </v-col>
                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model.trim="form.descricao"
                                    label="Descrição"
                                    outlined
                                    dense
                                    :rules="rules.descricao"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select
                                    v-model="form.status"
                                    :items="statusOptions"
                                    label="Status"
                                    outlined
                                    dense
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="form.data_manutencao"
                                    label="Data da manutenção"
                                    type="date"
                                    outlined
                                    dense
                                    :rules="rules.data_manutencao"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="form.data_conclusao"
                                    label="Data de conclusão"
                                    type="date"
                                    outlined
                                    dense
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model.trim="form.fornecedor"
                                    label="Fornecedor"
                                    outlined
                                    dense
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="form.quilometragem"
                                    label="Quilometragem"
                                    outlined
                                    dense
                                    inputmode="numeric"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                        <v-text-field
                                            v-model="form.valor"
                                            label="Valor"
                                            outlined
                                            dense
                                            prefix="R$"
                                            @input="formatCurrencyInput"
                                        />
                            </v-col>
                            <v-col cols="12">
                                <v-textarea
                                    v-model.trim="form.observacoes"
                                    label="Observações"
                                    outlined
                                    dense
                                    rows="3"
                                    auto-grow
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="px-6 pb-6">
                    <v-spacer />
                    <v-btn text :disabled="saving" @click="closeDialog">Cancelar</v-btn>
                    <v-btn class="manutencao-new-btn" depressed :loading="saving" :disabled="saving" @click="saveManutencao">
                        Salvar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
            <v-card class="manutencao-dialog" outlined>
                <v-card-title class="manutencao-dialog__title">
                    <span>Excluir manutenção</span>
                    <v-spacer />
                    <v-btn icon :disabled="deleting" @click="deleteDialogOpen = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider />
                <v-card-text class="pt-6 pb-2">
                    Tem certeza que deseja excluir <strong>{{ registroExclusao.descricao || '-' }}</strong>?
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-spacer />
                    <v-btn text :disabled="deleting" @click="deleteDialogOpen = false">Cancelar</v-btn>
                    <v-btn class="manutencao-delete-btn" depressed :loading="deleting" :disabled="deleting" @click="confirmarExclusao">
                        Excluir
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import manutencaoVeiculoServices from './manutencaoVeiculoServices';
import veiculoServices from '../veiculos/veiculoServices';

function getInitialForm() {
    return {
        id: null,
        veiculo_id: null,
        tipo: 'Preventiva',
        descricao: '',
        status: 'Aberta',
        data_manutencao: new Date().toISOString().slice(0, 10),
        data_conclusao: '',
        fornecedor: '',
        quilometragem: '',
        valor: '',
        observacoes: '',
    };
}

export default {
    name: 'ListagemManutencoesVeiculos',

    data() {
        return {
            loading: false,
            saving: false,
            deleting: false,
            dialogOpen: false,
            deleteDialogOpen: false,
            detalhesDialogOpen: false,
            detalhesSelecionado: null,
            formValid: false,
            manutencoes: [],
            veiculos: [],
            registroExclusao: {},
            form: getInitialForm(),
            tiposManutencao: ['Preventiva', 'Corretiva', 'Revisão', 'Troca de óleo', 'Pneus', 'Freios', 'Elétrica', 'Outro'],
            statusOptions: ['Aberta', 'Em andamento', 'Concluída', 'Cancelada'],
            filterDialogOpen: false,
            filterForm: {
                status: null,
                veiculo_id: null,
                tipo: null,
                data_de: '',
                data_ate: '',
            },
            activeFilters: {
                status: null,
                veiculo_id: null,
                tipo: null,
                data_de: '',
                data_ate: '',
            },
            headers: [
                { text: 'Veículo', value: 'veiculo', sortable: false },
                { text: 'Tipo', value: 'tipo', sortable: false },
                { text: 'Descrição', value: 'descricao', sortable: false },
                { text: 'Data', value: 'data_manutencao', sortable: false },
                { text: 'Status', value: 'status', sortable: false },
                { text: 'Valor', value: 'valor', sortable: false },
                { text: 'KM', value: 'quilometragem', sortable: false },
                { text: 'Ações', value: 'acoes', sortable: false, align: 'center' },
            ],
            footerProps: {
                itemsPerPageText: 'Itens por Página',
                itemsPerPageOptions: [10, 15, 20],
                pageText: '{0}-{1} de {2}'
            },
            rules: {
                veiculo_id: [(v) => Boolean(v) || 'Selecione o veículo'],
                tipo: [(v) => Boolean(String(v || '').trim()) || 'Informe o tipo'],
                descricao: [(v) => Boolean(String(v || '').trim()) || 'Informe a descrição'],
                data_manutencao: [(v) => Boolean(v) || 'Informe a data da manutenção'],
            },
        };
    },

    computed: {
        dialogTitle() {
            return this.form.id ? 'Editar manutenção' : 'Nova manutenção';
        },

        veiculoFilterOptions() {
            return this.veiculos.map((item) => ({
                label: `${item.placa} - ${item.modelo || 'Sem modelo'}`,
                value: item.id,
            }));
        },

        statusFiltroOptions() {
            return this.statusOptions;
        },

        tiposFiltroOptions() {
            return this.tiposManutencao;
        },

        hasActiveFilters() {
            const f = this.activeFilters;
            return Boolean(f.status || f.veiculo_id || f.tipo || f.data_de || f.data_ate);
        },

        manutencoesFiltradas() {
            const f = this.activeFilters;
            return this.manutencoes.filter((item) => {
                if (f.status && item.status !== f.status) return false;
                if (f.veiculo_id && item.veiculo_id !== f.veiculo_id) return false;
                if (f.tipo && item.tipo !== f.tipo) return false;
                const raw = item.data_manutencao ? String(item.data_manutencao).slice(0, 10) : '';
                if (f.data_de && (!raw || raw < f.data_de)) return false;
                if (f.data_ate && (!raw || raw > f.data_ate)) return false;
                return true;
            });
        },

        veiculosOptions() {
            return this.veiculos.map((item) => ({
                label: `${item.placa} - ${item.modelo || 'Sem modelo'}`,
                value: item.id,
            }));
        },

        totalEmAberto() {
            return this.manutencoes.filter((item) => !['Concluída', 'Cancelada'].includes(String(item.status || ''))).length;
        },

        totalConcluidas() {
            return this.manutencoes.filter((item) => String(item.status || '') === 'Concluída').length;
        },

        custoTotal() {
            return this.manutencoes.reduce((acc, item) => acc + (Number(item.valor) || 0), 0);
        },
    },

    methods: {
        abrirFiltros() {
            this.filterForm = { ...this.activeFilters };
            this.filterDialogOpen = true;
        },

        aplicarFiltros() {
            this.activeFilters = { ...this.filterForm };
            this.filterDialogOpen = false;
        },

        limparFiltros() {
            const empty = { status: null, veiculo_id: null, tipo: null, data_de: '', data_ate: '' };
            this.filterForm = { ...empty };
            this.activeFilters = { ...empty };
            this.filterDialogOpen = false;
        },

        async carregarDados() {
            this.loading = true;
            try {
                const [manutencoesResponse, veiculosResponse] = await Promise.all([
                    manutencaoVeiculoServices.listar(),
                    veiculoServices.listar(),
                ]);

                this.manutencoes = Array.isArray(manutencoesResponse?.data) ? manutencoesResponse.data : [];
                this.veiculos = Array.isArray(veiculosResponse?.data) ? veiculosResponse.data : [];
            } catch (error) {
                this.$toasted?.show(error?.response?.data?.error || 'Erro ao carregar manutenções.');
            } finally {
                this.loading = false;
            }
        },

        abrirDialogDetalhes(item) {
            this.detalhesSelecionado = item;
            this.detalhesDialogOpen = true;
        },

        abrirNovaManutencao() {
            this.form = getInitialForm();
            this.dialogOpen = true;
            this.formValid = false;
            this.$nextTick(() => this.$refs?.manutencaoForm?.resetValidation?.());
        },

        editarManutencao(item) {
            this.form = {
                id: item.id,
                veiculo_id: item.veiculo_id,
                tipo: item.tipo || 'Preventiva',
                descricao: item.descricao || '',
                status: item.status || 'Aberta',
                data_manutencao: this.formatDateInput(item.data_manutencao),
                data_conclusao: this.formatDateInput(item.data_conclusao),
                fornecedor: item.fornecedor || '',
                quilometragem: item.quilometragem === null || item.quilometragem === undefined ? '' : String(item.quilometragem),
                valor: item.valor === null || item.valor === undefined ? '' : Number(item.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                observacoes: item.observacoes || '',
            };

            this.dialogOpen = true;
            this.formValid = false;
            this.$nextTick(() => this.$refs?.manutencaoForm?.resetValidation?.());
        },

        formatDateInput(value) {
            if (!value) return '';
            // Se já está no formato yyyy-MM-dd
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
            // Se está no formato yyyy-MM-ddTHH:mm:ss ou yyyy-MM-dd HH:mm:ss
            if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
            // Se está no formato dd/MM/yyyy
            if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
                const [d, m, y] = value.split('/');
                return `${y}-${m}-${d}`;
            }
            // Tenta converter para Date
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        closeDialog() {
            this.dialogOpen = false;
            this.form = getInitialForm();
        },

        abrirDialogExcluir(item) {
            this.registroExclusao = item || {};
            this.deleteDialogOpen = true;
        },

        async confirmarExclusao() {
            if (!this.registroExclusao?.id) {
                return;
            }

            this.deleting = true;
            try {
                await manutencaoVeiculoServices.excluir(this.registroExclusao.id);
                this.$toasted?.show('Manutenção excluída com sucesso.');
                this.deleteDialogOpen = false;
                this.registroExclusao = {};
                await this.carregarDados();
            } catch (error) {
                this.$toasted?.show(error?.response?.data?.error || 'Erro ao excluir manutenção.');
            } finally {
                this.deleting = false;
            }
        },

        async saveManutencao() {
            const isValid = this.$refs?.manutencaoForm?.validate?.();
            if (!isValid) {
                return;
            }

            const payload = {
                veiculo_id: this.form.veiculo_id,
                tipo: this.form.tipo,
                descricao: this.form.descricao,
                status: this.form.status,
                data_manutencao: this.form.data_manutencao,
                data_conclusao: this.form.data_conclusao || null,
                fornecedor: this.form.fornecedor || null,
                quilometragem: this.form.quilometragem === '' ? null : this.form.quilometragem,
                valor: this.form.valor === '' ? null : this.parseCurrency(this.form.valor),
                observacoes: this.form.observacoes || null,
            };

            this.saving = true;
            try {
                if (this.form.id) {
                    await manutencaoVeiculoServices.atualizar(this.form.id, payload);
                    this.$toasted?.show('Manutenção atualizada com sucesso.');
                } else {
                    await manutencaoVeiculoServices.criar(payload);
                    this.$toasted?.show('Manutenção criada com sucesso.');
                }

                this.closeDialog();
                await this.carregarDados();
            } catch (error) {
                this.$toasted?.show(error?.response?.data?.error || 'Erro ao salvar manutenção.');
            } finally {
                this.saving = false;
            }
        },

        formatCurrencyInput() {
            const digitsOnly = String(this.form.valor || '').replace(/\D/g, '');
            if (!digitsOnly) {
                this.form.valor = '';
                return;
            }
            const centsValue = Number(digitsOnly) / 100;
            this.form.valor = centsValue.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },

        parseCurrency(value) {
            if (!value) {
                return 0;
            }
            const normalized = String(value)
                .replace(/\./g, '')
                .replace(',', '.');
            const parsed = Number(normalized);
            return Number.isFinite(parsed) ? parsed : 0;
        },

        formatCurrency(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }

            const numericValue = Number(value);
            if (!Number.isFinite(numericValue)) {
                return String(value);
            }

            return numericValue.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            });
        },

        formatDate(value) {
            if (!value) {
                return '-';
            }

            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) {
                return String(value);
            }

            return parsed.toLocaleDateString('pt-BR');
        },

        getStatusClass(status) {
            const valor = String(status || '').toLowerCase();
            if (valor === 'concluída' || valor === 'concluida') return 'is-success';
            if (valor === 'cancelada') return 'is-danger';
            if (valor === 'em andamento') return 'is-info';
            return 'is-warning';
        },
    },

    mounted() {
        this.carregarDados();
    },
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.manutencao-page {
    font-family: "Inter", sans-serif;
}

.manutencao-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    flex-wrap: wrap;
}

.manutencao-title {
    margin: 0;
    font-size: 22px;
    font-weight: 800;
    color: #10b981;
    line-height: 1.2;
}

.manutencao-subtitle {
    margin: 6px 0 0;
    max-width: 640px;
    color: #7a8ca6;
    font-size: 13px;
}

.manutencao-new-btn {
    background-color: #10b981 !important;
    color: #ffffff !important;
    border-radius: 12px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0.01em;
    padding: 10px 16px;
}

.manutencao-stat {
    border-radius: 16px !important;
    border-color: #e6ebf1 !important;
}

.manutencao-table-card__toolbar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 4px;
    padding: 0 12px;
    margin-top: -20px;
}

.manutencao-icon-btn {
    width: 38px;
    height: 38px;
    min-width: 38px !important;
    border-radius: 12px !important;
    background-color: #ffffff !important;
    color: #10b981 !important;
    border: 2px solid #10b981 !important;
}

.manutencao-table-card {
    border-radius: 16px !important;
    border-color: #e6ebf1 !important;
}

.manutencao-dialog {
    border-radius: 16px !important;
    border-color: #e6ebf1 !important;
}

.manutencao-stat__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px;
    gap: 12px;
}

.manutencao-stat__label {
    font-size: 12px;
    font-weight: 600;
    color: #7a8ca6;
}

.manutencao-stat__value {
    margin-top: 6px;
    font-size: 28px;
    font-weight: 800;
    color: #1f2a44;
}

.manutencao-stat__value--accent {
    color: #2563eb;
}

.manutencao-stat__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 14px;
    flex-shrink: 0;
}

.manutencao-stat__icon--primary {
    background: rgba(16, 185, 129, 0.12);
}

.manutencao-stat__icon--accent {
    background: rgba(37, 99, 235, 0.12);
}

.manutencao-stat__icon--muted {
    background: rgba(100, 116, 139, 0.12);
}

.manutencao-stat__icon--warning {
    background: rgba(245, 158, 11, 0.16);
}

.manutencao-table {
    border-radius: 16px;
}

.manutencao-table ::v-deep th {
    color: #7a8ca6 !important;
    font-weight: 800 !important;
    font-size: 11px !important;
    letter-spacing: 0.08em;
}

.manutencao-table ::v-deep td {
    color: #1f2a44;
    font-size: 13px;
}

.manutencao-table ::v-deep .v-data-table__wrapper {
    border-radius: 16px;
}

.manutencao-veiculo-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.manutencao-veiculo-cell span {
    font-size: 12px;
    color: #7a8ca6;
}

.manutencao-status {
    display: inline-flex;
    align-items: center;
    padding: 6px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
}

.manutencao-status.is-success {
    background: rgba(16, 185, 129, 0.12);
    color: #0f9f6e;
}

.manutencao-status.is-danger {
    background: rgba(239, 68, 68, 0.12);
    color: #dc2626;
}

.manutencao-status.is-info {
    background: rgba(37, 99, 235, 0.12);
    color: #2563eb;
}

.manutencao-status.is-warning {
    background: rgba(245, 158, 11, 0.16);
    color: #b45309;
}

.manutencao-actions {
    opacity: 0.9;
}

.manutencao-actions__menu {
    min-width: 180px;
}

.manutencao-dialog__title {
    color: #1f2a44;
    font-weight: 800;
}

.manutencao-delete-btn {
    background-color: #ef4444 !important;
    color: #ffffff !important;
    border-radius: 12px;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0.01em;
    padding: 10px 16px;
}
</style>